import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, IconButton, FormControlLabel, Switch, MenuItem, Autocomplete
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import axiosInstance from '../../services/axiosInstance';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';

interface Account {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
}

interface PaymentMethod {
  paymentMethodId: number;
  methodCode: string;
  methodName: string;
  methodNameAr: string;
  methodType: string;
  description?: string;
  isActive: boolean;
  displayOrder: number;
  accountId?: number;
}

interface PaymentMethodDialogProps {
  open: boolean;
  paymentMethod: PaymentMethod | null;
  onClose: (refresh?: boolean) => void;
}

const PaymentMethodDialog: React.FC<PaymentMethodDialogProps> = ({ open, paymentMethod, onClose }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [formData, setFormData] = useState({
    paymentMethodId: 0,
    methodCode: '',
    methodName: '',
    methodNameAr: '',
    methodType: 'Cash',
    description: '',
    isActive: true,
    displayOrder: 1,
    accountId: undefined as number | undefined
  });
  const [loading, setLoading] = useState(false);

  const t = {
    title: paymentMethod ? (isRTL ? 'تعديل طريقة دفع' : 'Edit Payment Method') : (isRTL ? 'إضافة طريقة دفع' : 'Add Payment Method'),
    code: isRTL ? 'الكود' : 'Code',
    name: isRTL ? 'الاسم' : 'Name',
    nameAr: isRTL ? 'الاسم بالعربية' : 'Arabic Name',
    type: isRTL ? 'النوع' : 'Type',
    account: isRTL ? 'الحساب المحاسبي' : 'Account',
    description: isRTL ? 'الوصف' : 'Description',
    displayOrder: isRTL ? 'الترتيب' : 'Display Order',
    active: isRTL ? 'نشط' : 'Active',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    saveSuccess: isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully',
    requiredFields: isRTL ? 'جميع الحقول مطلوبة' : 'All fields are required',
    cash: isRTL ? 'نقدي' : 'Cash',
    card: isRTL ? 'بطاقة' : 'Card',
    app: isRTL ? 'تطبيق' : 'App',
    bank: isRTL ? 'بنك' : 'Bank',
    other: isRTL ? 'أخرى' : 'Other'
  };

  useEffect(() => {
    if (open) {
      loadAccounts();
      if (paymentMethod) {
        setFormData({
          paymentMethodId: paymentMethod.paymentMethodId,
          methodCode: paymentMethod.methodCode,
          methodName: paymentMethod.methodName,
          methodNameAr: paymentMethod.methodNameAr,
          methodType: paymentMethod.methodType,
          description: paymentMethod.description || '',
          isActive: paymentMethod.isActive,
          displayOrder: paymentMethod.displayOrder,
          accountId: paymentMethod.accountId
        });
      } else {
        generateNextCode();
      }
    }
  }, [open, paymentMethod]);

  const loadAccounts = async () => {
    try {
      const data = await chartOfAccountsService.getAll();
      const finalAccounts = data.filter((acc: any) => 
        acc.isFinalAccount && acc.isActive
      );
      setAccounts(finalAccounts);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const generateNextCode = async () => {
    try {
      const response = await axiosInstance.get('/PaymentMethods');
      const methods = response.data;
      let nextCode = 'PM-001';
      if (methods.length > 0) {
        const codes = methods.map((m: any) => m.methodCode || m.MethodCode);
        const numbers = codes.filter((code: string) => code && code.startsWith('PM-')).map((code: string) => parseInt(code.replace('PM-', '')) || 0);
        const maxNumber = Math.max(...numbers, 0);
        nextCode = `PM-${(maxNumber + 1).toString().padStart(3, '0')}`;
      }
      setFormData(prev => ({ ...prev, paymentMethodId: 0, methodCode: nextCode, methodName: '', methodNameAr: '', methodType: 'Cash', description: '', isActive: true, displayOrder: 1, accountId: undefined }));
    } catch (error) {
      setFormData(prev => ({ ...prev, methodCode: 'PM-001' }));
    }
  };

  const handleSave = async () => {
    if (!formData.methodCode.trim() || !formData.methodName.trim() || !formData.methodNameAr.trim()) {
      toast.error(t.requiredFields);
      return;
    }
    try {
      setLoading(true);
      const payload = { ...formData };
      if (paymentMethod) {
        await axiosInstance.put(`/PaymentMethods/${paymentMethod.paymentMethodId}`, payload);
      } else {
        await axiosInstance.post('/PaymentMethods', payload);
      }
      toast.success(t.saveSuccess);
      onClose(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || (isRTL ? 'فشلت العملية' : 'Operation failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#1976D2', color: 'white', fontWeight: 'bold' }}>
        {t.title}
        <IconButton onClick={() => onClose(false)} size="small" sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField label={t.code} value={formData.methodCode} onChange={(e) => setFormData({ ...formData, methodCode: e.target.value })} required fullWidth disabled={!!paymentMethod} />
            <TextField label={t.type} select value={formData.methodType} onChange={(e) => setFormData({ ...formData, methodType: e.target.value })} required fullWidth>
              <MenuItem value="Cash">{t.cash}</MenuItem>
              <MenuItem value="Card">{t.card}</MenuItem>
              <MenuItem value="App">{t.app}</MenuItem>
              <MenuItem value="Bank">{t.bank}</MenuItem>
              <MenuItem value="Other">{t.other}</MenuItem>
            </TextField>
            <TextField label={t.name} value={formData.methodName} onChange={(e) => setFormData({ ...formData, methodName: e.target.value })} required fullWidth />
            <TextField label={t.nameAr} value={formData.methodNameAr} onChange={(e) => setFormData({ ...formData, methodNameAr: e.target.value })} required fullWidth />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={accounts}
              getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName}`}
              value={accounts.find(a => a.accountId === formData.accountId) || null}
              onChange={(_, newValue) => setFormData({ ...formData, accountId: newValue?.accountId })}
              renderInput={(params) => <TextField {...params} label={t.account} />}
              fullWidth
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <TextField label={t.description} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={3} fullWidth />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
            <TextField label={t.displayOrder} type="number" value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 1 })} fullWidth inputProps={{ min: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} color="primary" />} label={t.active} />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => onClose(false)} disabled={loading}>{t.cancel}</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} disabled={loading} sx={{ bgcolor: '#1976D2', '&:hover': { bgcolor: '#1565C0' } }}>
          {loading ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentMethodDialog;